import React, { useState } from 'react';
import { useNavigate, NavLink } from "react-router-dom";

// Firebase imports
import { auth, db } from "../../firebase/config";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, query, where, getDocs, limit } from "firebase/firestore";

/**
 * Login - Componente de acceso con seguridad reforzada.
 * Diseño modernizado con Tailwind CSS.
 */
const Login = () => {
    const navigate = useNavigate();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!email || !password) {
            setError("Por favor, complete todos los campos.");
            return;
        }

        setError(null);
        setLoading(true);

        const cleanEmail = email.trim().toLowerCase();

        try {
            // 1. Autenticación inicial
            const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
            const user = userCredential.user;
            
            // 2. Validación de Negocio
            const colabRef = collection(db, 'public/data/colaboradores');
            const q = query(colabRef, where('email', '==', user.email), limit(1));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                await signOut(auth);
                throw { code: 'auth/unauthorized-colab' };
            }

            const colabDoc = snapshot.docs[0];
            const colabData = colabDoc.data();

            // 3. Verificación de Estado de Cuenta
            if (colabData.activo === false) {
                await signOut(auth);
                throw { code: 'auth/account-disabled' };
            }

            // 4. Perfil de Sesión
            const userProfile = {
                uid: user.uid,
                id: colabDoc.id,
                email: user.email,
                nombre: colabData.nombre || 'Usuario',
                rol: colabData.rol || 'Invitado',
                activo: true
            };

            localStorage.setItem('userProfile', JSON.stringify(userProfile));
            navigate('/cpanel');

        } catch (error) {
            console.error("Login Security Audit:", error.code || error.message);
            
            const errorMap = {
                'auth/invalid-email': "Email o contraseña incorrectos.",
                'auth/user-not-found': "Email o contraseña incorrectos.",
                'auth/wrong-password': "Email o contraseña incorrectos.",
                'auth/invalid-credential': "Email o contraseña incorrectos.",
                'auth/too-many-requests': "Demasiados intentos fallidos. Intente más tarde.",
                'auth/account-disabled': "Su cuenta ha sido deshabilitada por un administrador.",
                'auth/unauthorized-colab': "Acceso denegado: No figura como colaborador autorizado."
            };

            setError(errorMap[error.code] || "Error al iniciar sesión. Verifique su conexión.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-slate-50 font-sans text-slate-800">
            
            {/* PANEL IZQUIERDO - IMAGEN DE FONDO (Oculto en móviles) */}
            <div className="hidden lg:flex w-1/2 relative bg-white overflow-hidden">
                {/* Imagen sin el filtro oscuro */}
                <img 
                    src="/imagenes/imag-inicial.jpg" 
                    alt="Bienvenido a IB Brokers" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Gradiente blanco suave arriba hacia abajo para asegurar que el texto se lea perfecto */}
                <div className="absolute inset-0 bg-gradient-to-b from-white via-white/80 to-transparent h-2/3"></div>
                
                {/* Contenedor de texto alineado arriba (justify-start) y con texto oscuro */}
                <div className="relative z-10 flex flex-col justify-start p-16 h-full w-full mt-0">
                    <h2 className="text-4xl font-bold mb-4 tracking-tight text-slate-900">Hola 👋 ¡Bienvenido a IB Online!</h2>
                    <p className="text-lg text-slate-700 max-w-lg font-medium">
                        Plataforma integral de gestión de pólizas y contratos.
                    </p>
                </div>
            </div>

            {/* PANEL DERECHO - FORMULARIO DE LOGIN */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md bg-white p-10 sm:p-12 rounded-2xl shadow-xl border border-slate-100 transition-all">
                    
                    {/* Header del Formulario */}
                    <div className="text-center mb-10">
                        <img 
                            src="/imagenes/Logo.svg" 
                            alt="Logo IB Brokers" 
                            className="h-20 mx-auto mb-6 object-contain"
                        />
                        <h1 className="text-2xl font-bold text-slate-900">Iniciar Sesión</h1>
                        <p className="text-sm text-slate-500 mt-2">Ingrese sus credenciales para continuar</p>
                    </div>
                    
                    <form onSubmit={handleLogin} className="space-y-6" noValidate>
                        
                        {/* Input Email */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Correo Electrónico</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                </div>
                                <input 
                                    type="email" 
                                    className={`w-full pl-10 pr-4 py-3 bg-slate-50 border ${error ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-600'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
                                    placeholder="ejemplo@ibbrokers.com" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                        </div>
                        
                        {/* Input Password */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Contraseña</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                </div>
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    className={`w-full pl-10 pr-12 py-3 bg-slate-50 border ${error ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-600'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
                                    placeholder="••••••••" 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                                {/* Botón Ver/Ocultar Contraseña */}
                                <button 
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none"
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                                    )}
                                </button>
                            </div>
                        </div>
                        
                        {/* Mensaje de Error */}
                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 animate-pulse">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
                                <p>{error}</p>
                            </div>
                        )}
                        
                        {/* Botón de Submit */}
                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center shadow-md shadow-blue-500/30"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Validando credenciales...
                                </div>
                            ) : (
                                'Iniciar Sesión'
                            )}
                        </button>
                        
                        {/* Enlace Recuperar Contraseña */}
                        <div className="text-center pt-2">
                            <NavLink 
                                to="/reset-password" 
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                ¿Olvidaste tu contraseña?
                            </NavLink>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;